import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useRundowns } from '../../hooks/useRundowns';
import { useCreateRundown, useDeleteRundown } from '../../hooks/useRundown';
import { ListMusic, Plus, Calendar, Trash2, Clock, FileText } from 'lucide-react';
import type { RundownStatus } from '@news-assist/shared';

const statusColors: Record<RundownStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  ready: 'bg-blue-100 text-blue-700',
  recorded: 'bg-purple-100 text-purple-700',
  published: 'bg-green-100 text-green-700',
};

const statusLabels: Record<RundownStatus, string> = {
  draft: 'Draft',
  ready: 'Ready',
  recorded: 'Recorded',
  published: 'Published',
};

export function RundownsPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';
  const navigate = useNavigate();
  const { data, isLoading } = useRundowns();
  const createRundown = useCreateRundown();
  const deleteRundown = useDeleteRundown();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTargetDate, setNewTargetDate] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const rundowns = data?.rundowns || [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    try {
      const result = await createRundown.mutateAsync({
        title: newTitle,
        targetDate: newTargetDate || undefined,
      });
      setShowNewForm(false);
      setNewTitle('');
      setNewTargetDate('');
      navigate(`${orgPrefix}/rundown/${result.id}`);
    } catch (err) {
      alert('Failed to create rundown: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Delete this rundown?')) return;

    setDeletingId(id);
    try {
      await deleteRundown.mutateAsync(id);
    } catch (err) {
      alert('Failed to delete: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ListMusic className="w-6 h-6" />
            Podcast Rundowns
          </h1>
          <p className="text-gray-600 mt-1">
            Plan and arrange stories for your podcast episodes
          </p>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Rundown
        </button>
      </div>

      {/* New Rundown Modal */}
      {showNewForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Rundown</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Monday Morning News"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Date (optional)</label>
                  <input
                    type="date"
                    value={newTargetDate}
                    onChange={(e) => setNewTargetDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createRundown.isPending || !newTitle.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createRundown.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rundowns List */}
      {isLoading ? (
        <div className="text-gray-500">Loading rundowns...</div>
      ) : rundowns.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <ListMusic className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No rundowns yet</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create your first rundown
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rundowns.map((rundown) => (
            <Link
              key={rundown.id}
              to={`${orgPrefix}/rundown/${rundown.id}`}
              className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[rundown.status]}`}>
                      {statusLabels[rundown.status]}
                    </span>
                    {rundown.targetDate && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {rundown.targetDate}
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-900">{rundown.title}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {rundown.storyCount} {rundown.storyCount === 1 ? 'story' : 'stories'}
                    </span>
                    <span>{rundown.claimCount} claims</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {rundown.createdAt ? new Date(rundown.createdAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => handleDelete(rundown.id, e)}
                  disabled={deletingId === rundown.id}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Delete rundown"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
